import React, { useState } from 'react';
import { ThemeProvider as StyledProvider } from 'styled-components';
import { createGenerateClassName, StylesProvider, ThemeProvider as MuiProvider } from '@material-ui/core/styles';
import { MockedProvider } from '@apollo/client/testing';
import { RenderOptions, render as nativeRender } from '@testing-library/react';

import { getStyledTheme } from 'src/theme/builders/styled';
import { getMuiTheme } from 'src/theme/builders/mui';

import { buildScale, getColors, shadows, SPACES_INTERVAL, SPACES_SCALE_DEGREES } from 'src/theme';
import { ColorsProvider } from 'src/contexts/colors';
import { StoresProvider } from 'src/contexts/stores';
import { ViewportProvider } from 'src/contexts/viewport';
import QueryParamProvider from 'src/utils/query-param-provider';
import useCreateStores from 'src/state';
import { CommerceComponentsContext, defaultCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { CatalogRequestProvider } from 'src/contexts/catalog-request-context';

import { TestRouterProvider } from './test-router-provider';

export function TestProvider(props): JSX.Element {
  // useCreateStores() comes from an untyped JS module and is inferred as `undefined`, so cast
  // it to a spreadable record; `props` overrides these defaults in the merge below.
  const builtInStores = (useCreateStores() as unknown) as Record<string, unknown>;

  // MUI v4 derives class-name suffixes (e.g. `PrivateNotchedOutline-root-7`) from a
  // module-global counter that advances on every styled render, and TestProvider builds a
  // fresh MUI theme per render — so the number landed on a different value depending on how
  // many tests ran first, making the DOM snapshots order-dependent and flaky. Giving each
  // test its own JSS generator + sheets cache resets the counter, so the numbering is
  // deterministic regardless of run order. Passed to render() via the `wrapper` option so
  // each test still renders the component under test directly.
  const [generateClassName] = useState(createGenerateClassName);
  const [sheetsManager] = useState(new Map());

  const {
    cart,
    catalogRequest,
    children,
    colors = getColors(),
    DutchiePayEnrollment,
    InstoreCart,
    market,
    mocks = [],
    mockStores,
    Modals,
    pusher,
    router,
    searchPreferences,
    UI,
    user,
  } = {
    ...builtInStores,
    ...props,
  };

  const customizableTheme = {
    colors: {
      navBar: '#fff',
      buttonsLinks: '#0b99e6',
      discountTag: '#f3583a',
    },
    fonts: {
      primary: 'Primary Font',
      secondary: 'Secondary Font',
    },
    utils: {
      isNavBarDark: false,
    },
    textTransforms: {
      buttons: 'none',
      links: 'none',
      tags: 'none',
    },
    radius: {
      sm: '0',
      md: '0',
      lg: '0',
      xl: '0',
      buttons: '0',
      tags: '0',
      cards: '0',
    },
  };

  const muiTheme = getMuiTheme({ colors, customized: customizableTheme });
  const styledTheme = getStyledTheme({
    colors,
    // Tests supply no embedded-menu overrides; production passes a real value here.
    embeddedMenu: undefined,
    breakpoints: muiTheme.breakpoints,
    shadows,
    spaces: buildScale(SPACES_INTERVAL, SPACES_SCALE_DEGREES),
    customized: customizableTheme,
  });

  return (
    <TestRouterProvider router={router}>
      <MockedProvider mocks={mocks} addTypename={false}>
        <StoresProvider
          value={{
            UI,
            user,
            searchPreferences,
            market,
            cart,
            pusher,
            Modals,
            DutchiePayEnrollment,
            InstoreCart,
            ...mockStores,
          }}
        >
          <CatalogRequestProvider
            value={
              catalogRequest ?? {
                menuType: cart?.menuType ?? 'rec',
                orderType: cart?.simplifiedOrderType ?? '',
                ready: true,
                surface: 'consumer',
              }
            }
          >
            <ColorsProvider value={colors}>
              <StyledProvider theme={styledTheme}>
                <MuiProvider theme={muiTheme}>
                  <CommerceComponentsContext.Provider value={defaultCommerceComponents}>
                    <ViewportProvider>
                      {/* Mirror pages/_app.jsx so components using use-query-params (v2 requires a
                        provider) get the query-param context in tests. */}
                      <QueryParamProvider>
                        <StylesProvider generateClassName={generateClassName} sheetsManager={sheetsManager}>
                          {children}
                        </StylesProvider>
                      </QueryParamProvider>
                    </ViewportProvider>
                  </CommerceComponentsContext.Provider>
                </MuiProvider>
              </StyledProvider>
            </ColorsProvider>
          </CatalogRequestProvider>
        </StoresProvider>
      </MockedProvider>
    </TestRouterProvider>
  );
}

// Custom render that wraps every component under test in TestProvider by default, while
// still accepting all of @testing-library/react's render options (a caller-supplied
// `wrapper` overrides the default). Typed as `typeof nativeRender` so the signature stays
// 1:1 with the native render — same overloads, same generics, same return types.
export const render = ((ui: React.ReactElement, options?: RenderOptions) =>
  nativeRender(ui, { wrapper: TestProvider, ...options })) as typeof nativeRender;
